import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from 'react-toastify';
import Select from "react-select";
import { FiTag } from "react-icons/fi";

const API_URL = "https://ims.holdthechild.net";

const Section = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
        <div className="border border-gray-200 rounded-lg mb-4 shadow-sm">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 font-semibold text-gray-800 flex justify-between items-center rounded-t-lg"
            >
                {title}
                <span className="text-orange-600 text-xl">{isOpen ? "−" : "+"}</span>
            </button>
            {isOpen && <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">{children}</div>}
        </div>
    );
};

const FormField = ({ label, name, value, onChange, type = "text", placeholder = "", options = null, required = false, className = "", disabled = false }) => (
    <div className={className}>
        <label htmlFor={name} className="block mb-1 font-medium text-gray-700">
            {label}
        </label>
        {type === 'select' ? (
            <select id={name} name={name} value={value} onChange={onChange} className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500" disabled={disabled}>
                {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
        ) : type === 'textarea' ? (
            <textarea id={name} name={name} value={value} onChange={onChange} placeholder={placeholder} rows="3" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" disabled={disabled} />
        ) : (
            // FIXED: Changed onChange={handleChange} to onChange={onChange}
            <input id={name} name={name} type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" required={required} disabled={disabled} />
        )}
    </div>
);

export default function EditAssetModal({ assetId, closeModal, onAssetUpdated }) {
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [staffList, setStaffList] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const [vendorList, setVendorList] = useState([]);

    const staffOptions = staffList.map((staff) => ({
        value: staff._id,
        label: `${staff.firstName} ${staff.lastName}`,
        department: staff.department || "",
    }));

    const projectOptions = projectList.map((project) => ({
        value: project._id,
        label: project.projectTitle,
        assignedProgramManager: project.assignedProgramManager?._id || null,
        assignedProgramManagerName: project.assignedProgramManager ? `${project.assignedProgramManager.firstName} ${project.assignedProgramManager.lastName}` : 'N/A',
        budgetCode: project.budgetCode // Ensure budget code is in options
    }));

    const vendorOptions = vendorList.map((vendor) => ({
        value: vendor._id,
        label: vendor.name,
    }));

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Authentication token not found. Please log in.");
                return;
            }
            try {
                const staffRes = await axios.get(`${API_URL}/api/staff`, { headers: { Authorization: `Bearer ${token}` } });
                setStaffList(staffRes.data.data || []);

                const projectRes = await axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } });
                setProjectList(projectRes.data.data || []);
                
                const vendorRes = await axios.get(`${API_URL}/api/vendors`, { headers: { Authorization: `Bearer ${token}` } });
                setVendorList(vendorRes.data.data || []);
            } catch (err) {
                console.error("Failed to fetch data:", err);
                toast.error("Failed to load staff or project list. 😢");
            }
        };
        fetchData();
    }, []);

    const fetchAssetDetails = useCallback(async () => {
        setFetching(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");
            const response = await axios.get(`${API_URL}/api/assets/${assetId}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });
            const assetData = response.data.data;

            // Normalize fields
            if (assetData.purchaseDate) {
                assetData.purchaseDate = new Date(assetData.purchaseDate).toISOString().split('T')[0];
            }
            if (assetData.lastServiced) {
                assetData.lastServiced = new Date(assetData.lastServiced).toISOString().split('T')[0];
            }
            if (assetData.expectedEndOfLife) {
                assetData.expectedEndOfLife = new Date(assetData.expectedEndOfLife).toISOString().split('T')[0];
            }

            setFormData({
                ...assetData,
                assignedTo: assetData.assignedTo?._id || null,
                custodian: assetData.custodian?._id || null,
                unitPrice: assetData.unitPrice || "",
                comprehensivePrice: assetData.comprehensivePrice || "",
                quantity: assetData.quantity || 1,
                projectId: assetData.projectId?._id || null,
                vendor: assetData.vendor?._id || null,
                budgetCode: assetData.budgetCode || "", // <-- Load Budget Code
            });
        } catch (err) {
            toast.error("Failed to fetch asset details. 😢");
            console.error(err);
        } finally {
            setFetching(false);
        }
    }, [assetId]);

    useEffect(() => {
        fetchAssetDetails();
    }, [fetchAssetDetails]);

    // --- UPDATED: Effect to auto-populate custodian AND budget code when project is selected ---
    useEffect(() => {
        if (formData && formData.projectId) {
            const selectedProject = projectList.find(p => p._id === formData.projectId);
            if (selectedProject) {
                setFormData(prev => ({
                    ...prev,
                    custodian: selectedProject.assignedProgramManager?._id || null,
                    budgetCode: selectedProject.budgetCode || "", // Auto-pull Budget Code
                }));
            } else {
                setFormData(prev => ({ 
                    ...prev, 
                    custodian: null,
                    budgetCode: "" 
                }));
            }
        } else if (formData) {
            // If project is cleared
            setFormData(prev => ({ 
                ...prev, 
                custodian: null,
                budgetCode: "" 
            }));
        }
    }, [formData?.projectId, projectList]); 
    // -------------------------------------------------------------------------------------------

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newForm = { ...prev, [name]: value };

            if (name === "quantity" || name === "unitPrice") {
                const qty = Number(newForm.quantity);
                const unitPrice = Number(newForm.unitPrice);
                if (qty > 0 && unitPrice > 0) {
                    newForm.comprehensivePrice = (qty * unitPrice).toFixed(2);
                } else {
                    newForm.comprehensivePrice = "";
                }
            }
            if (name === "quantity" || name === "comprehensivePrice") {
                const qty = Number(newForm.quantity);
                const comprehensivePrice = Number(newForm.comprehensivePrice);
                if (qty > 0 && comprehensivePrice > 0) {
                    newForm.unitPrice = (comprehensivePrice / qty).toFixed(2);
                } else {
                    newForm.unitPrice = "";
                }
            }

            return newForm;
        });
    };

    const handleStaffSelect = (fieldName, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleProjectSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            projectId: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleSelectChange = (fieldName, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const token = localStorage.getItem("token");
        const submissionData = {
            ...formData,
            purchaseCost: Number(formData.comprehensivePrice),
            unitPrice: Number(formData.unitPrice),
            comprehensivePrice: Number(formData.comprehensivePrice),
            vendor: formData.vendor,
        };

        try {
            if (!token) throw new Error("Authentication token not found.");

            await axios.put(`${API_URL}/api/assets/${assetId}`, submissionData, {
                headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            });

            toast.success("Asset updated successfully! 🎉");
            onAssetUpdated();
            closeModal();
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "An unexpected error occurred.";
            toast.error(`Error: ${errorMessage} 😥`);
        } finally {
            setLoading(false);
        }
    };

    if (fetching || !formData) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
                <div className="text-white text-xl">Loading Asset Data...</div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                    <h2 className="text-2xl font-bold text-orange-600">Edit Asset</h2>
                    <button type="button" onClick={closeModal} className="text-3xl text-gray-400 hover:text-red-500 font-light">&times;</button>
                </div>

                <Section title="Asset Details">
                    <FormField label="Asset Name" name="name" value={formData.name || ''} onChange={handleChange} placeholder="e.g., Laptop, Projector" required />
                    <FormField label="Category" name="category" type="select" value={formData.category || ''} onChange={handleChange} options={[
                        { value: "Electronics", label: "Electronics" }, { value: "Furniture", label: "Furniture" },
                        { value: "Vehicles", label: "Vehicles" }, { value: "Office Supplies", label: "Office Supplies" },
                        { value: "Other", label: "Other" },
                    ]} />
                    
                    {/* --- UPDATED: Status Field with Warning --- */}
                    <div className="sm:col-span-2">
                        <label className="block mb-1 font-medium text-gray-700">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="Active">Active</option>
                            <option value="Pending Acceptance">Pending Acceptance</option>
                            <option value="Needs Review">Needs Review</option>
                            <option value="Under Maintenance">Under Maintenance</option>
                            <option value="Damaged">Damaged</option>
                            <option value="Disposed">Disposed</option>
                            <option value="Pending Return">Pending Return</option>
                            <option value="Returned">Returned</option>
                        </select>
                        {/* Warning Text */}
                        { (formData.assignedTo || formData.custodian) && formData.status !== 'Pending Acceptance' && (
                             <p className="text-xs text-orange-500 mt-2 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path></svg>
                                Changing assignment will override status to "Pending Acceptance".
                             </p>
                        )}
                    </div>
                    {/* -------------------------------------- */}

                    <FormField label="Condition" name="condition" type="select" value={formData.condition || ''} onChange={handleChange} options={[
                        { value: "New", label: "New" }, { value: "Good", label: "Good" }, { value: "Needs Repair", label: "Needs Repair" }, { value: "Scrap", label: "Scrap" },
                    ]} />
                </Section>

                <Section title="Acquisition & Quantity">
                    <FormField label="Purchase Date" name="purchaseDate" type="date" value={formData.purchaseDate || ''} onChange={handleChange} />
                    <FormField label="Purchase Cost ($)" name="unitPrice" type="number" value={formData.unitPrice || ''} onChange={handleChange} placeholder="e.g., 500" />
                    <div className="sm:col-span-2">
                        <label className="block mb-1 font-medium text-gray-700">Vendor</label>
                        <Select
                            options={vendorOptions}
                            value={vendorOptions.find(option => option.value === formData.vendor)}
                            onChange={(selected) => handleSelectChange('vendor', selected)}
                            placeholder="Select Vendor..."
                            isClearable
                            className="react-select-container" classNamePrefix="react-select"
                        />
                    </div>
                </Section>

                <Section title="Maintenance & Lifecycle">
                    <FormField
                        label="Last Serviced"
                        name="lastServiced"
                        type="date"
                        value={formData.lastServiced || ''}
                        onChange={handleChange}
                    />
                    <FormField
                        label="Expected End of Life"
                        name="expectedEndOfLife"
                        type="date"
                        value={formData.expectedEndOfLife || ''}
                        onChange={handleChange}
                    />
                </Section>

                <Section title="Assignment & Location">
                    {/* Project Selection */}
                    <div className="sm:col-span-2">
                        <label className="block mb-1 font-medium text-gray-700">Assign to Project</label>
                        <Select
                            options={projectOptions}
                            value={projectOptions.find(option => option.value === formData.projectId)}
                            onChange={handleProjectSelect}
                            placeholder="Select Project (Optional)..."
                            isClearable
                            className="react-select-container" classNamePrefix="react-select"
                        />
                    </div>

                    {/* --- NEW: Budget Code Display --- */}
                    <div className="sm:col-span-2">
                        <label className="block mb-1 font-medium text-gray-700">Budget Code (Auto-Pulled)</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiTag className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                name="budgetCode"
                                value={formData.budgetCode}
                                readOnly
                                className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-300 bg-orange-50 text-orange-800 font-mono focus:outline-none"
                                placeholder="Select a project to see code"
                            />
                        </div>
                    </div>
                    {/* ---------------------------- */}

                    <div>
                        <label className="block mb-1 font-medium text-gray-700">Assigned To</label>
                        <Select
                            options={staffOptions}
                            value={staffOptions.find(option => option.value === formData.assignedTo)}
                            onChange={(selected) => handleStaffSelect('assignedTo', selected)}
                            placeholder="Select Staff..."
                            isClearable
                            className="react-select-container" classNamePrefix="react-select"
                        />
                    </div>
                    <div>
                        <label className="block mb-1 font-medium text-gray-700">Custodian</label>
                        <Select
                            options={staffOptions}
                            value={staffOptions.find(option => option.value === formData.custodian)}
                            onChange={(selected) => handleStaffSelect('custodian', selected)}
                            placeholder="Select Staff..."
                            isClearable
                            isDisabled={!!formData.projectId}
                            className="react-select-container" classNamePrefix="react-select"
                        />
                        {formData.projectId && (
                            <p className="text-xs text-gray-500 mt-1">
                                Custodian is auto-assigned from the selected project's Program Manager.
                            </p>
                        )}
                    </div>
                    <FormField label="Location" name="location" value={formData.location || ''} onChange={handleChange} placeholder="e.g., Head Office, Field Site A" />
                </Section>

                <Section title="Additional Notes">
                    <FormField label="Notes" name="notes" type="textarea" value={formData.notes || ''} onChange={handleChange} placeholder="Any relevant information about the asset..." className="sm:col-span-2" />
                </Section>

                <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-200">
                    <button type="button" onClick={closeModal} disabled={loading} className="px-5 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400">
                        Cancel
                    </button>
                    <button type="submit" disabled={loading} className="px-5 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:bg-orange-400">
                        {loading ? "Updating..." : "Save Changes"}
                    </button>
                </div>
            </form>
        </div>
    );
}